import { useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Segmented,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router';
import type { ArtigoNoAdmin, RecorteDaLei } from '@titularis/api-client';
import { lerPagina, useEstadoDeLista } from '@titularis/ui';
import { api } from '../../api';
import { GavetaDoComentario, type ArtigoNaGaveta } from './GavetaDoComentario';
import { ROTULO } from './estados';

/**
 * Uma lei, artigo a artigo, como o admin a vê.
 *
 * **É a tela que faltava para o artigo que ninguém cobrou.** O backlog nasce da
 * incidência: um artigo que questão nenhuma cita não aparecia em lista alguma, e
 * não havia por onde pedir o comentário dele - ainda que alguém saiba que ele
 * importa. Aqui a lei aparece na ordem em que se lê, com o estado de cada
 * artigo, e a decisão é tomada sobre ela.
 *
 * **Uma rota própria, e não uma quarta aba.** A lei tem endereço - dá para
 * mandar "olha a 8.935" a alguém -, e uma aba que só faz sentido depois de
 * escolher uma lei ficaria vazia para quem chegasse nela pela barra.
 */

const useCompacto = () => !Grid.useBreakpoint().md;

const TAMANHOS_DE_PAGINA = [25, 50, 100];

/*
 * Cinquenta por página, e não vinte: aqui se varre a lei - a leitura é
 * sequencial e o artigo procurado costuma estar perto do que se está vendo.
 */
const POR_PAGINA_PADRAO = 50;

/**
 * As larguras das colunas da tabela, que também são a conta da rolagem.
 *
 * **`scroll={{ x: true }}` escondia a ação.** Com `true` o Ant Design deixa a
 * tabela em `width: auto`, e a coluna do texto - a única sem largura, e que não
 * quebra linha porque tem reticência - estica a tabela até o caput inteiro
 * caber numa linha só: 3.300px de tabela numa área de 1.150. Tudo o que vem
 * depois dela, situação e ação inclusive, sai do campo de visão atrás de uma
 * rolagem horizontal que ninguém adivinha que precisa fazer. Com um número, a
 * tabela para de crescer, o `minWidth: 100%` do Antd a faz preencher a área, e
 * a reticência volta a cortar o caput onde deve.
 */
const LARGURA = { artigo: 110, questoes: 110, situacao: 130, acao: 160 };

/**
 * O piso da coluna do texto, que não tem largura fixa: ela fica com a sobra, e
 * a sobra pode ser toda a tela. O piso é o que entra na conta da rolagem, para
 * o caput não sumir quando a janela é a menor que ainda mostra a tabela.
 */
const PISO_DO_TEXTO = 280;

const LARGURA_MINIMA =
  PISO_DO_TEXTO + Object.values(LARGURA).reduce((soma, largura) => soma + largura, 0);

const RECORTES: { value: RecorteDaLei; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'cobrados', label: 'Cobrados na prova' },
  { value: 'comentados', label: 'Com comentário' },
];

export function LeiPage() {
  const { id } = useParams<{ id: string }>();
  const compacto = useCompacto();
  const navegar = useNavigate();
  const { message } = App.useApp();
  const clienteDeConsultas = useQueryClient();
  const { filtrar, irParaPagina, busca } = useEstadoDeLista();

  const [aberto, setAberto] = useState<ArtigoNaGaveta | null>(null);
  const [pedindo, setPedindo] = useState<ArtigoNoAdmin | null>(null);
  const [briefing, setBriefing] = useState('');

  // O que vem da URL é digitável à mão: recorte inventado cai no padrão em vez
  // de virar consulta que a API recusa.
  const params = new URLSearchParams(busca);
  const naUrl = params.get('recorte');
  const recorte = RECORTES.some((r) => r.value === naUrl) ? (naUrl as RecorteDaLei) : 'todos';
  const termo = params.get('termo') ?? '';
  const porPaginaNaUrl = Number(params.get('porPagina'));
  const porPagina = TAMANHOS_DE_PAGINA.includes(porPaginaNaUrl)
    ? porPaginaNaUrl
    : POR_PAGINA_PADRAO;
  const page = lerPagina(params);

  const lei = useQuery({
    queryKey: ['law-articles', id, { recorte, termo, page, porPagina }],
    queryFn: () =>
      api.listLawArticles(id!, {
        recorte: recorte === 'todos' ? undefined : recorte,
        termo: termo || undefined,
        page,
        pageSize: porPagina,
      }),
    enabled: Boolean(id),
  });

  const invalidar = () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['law-articles'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['article-backlog'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['commented-articles'] }),
    ]);

  const pedir = useMutation({
    mutationFn: (dados: { artigo: ArtigoNoAdmin; briefing?: string }) =>
      api.requestArticleCommentary({
        artigos: [{ legislationId: id!, article: dados.artigo.artigo }],
        ...(dados.briefing ? { briefing: dados.briefing } : {}),
      }),
    onSuccess: async (resultado) => {
      if (resultado.pedidos.length === 0) {
        message.warning('O artigo já está com um motor ou publicado.');
      } else {
        message.success('Na lista. O motor o pega nas próximas chamadas.');
      }
      setPedindo(null);
      setBriefing('');
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const cancelar = useMutation({
    mutationFn: (artigo: ArtigoNoAdmin) =>
      api.cancelArticleRequest([{ legislationId: id!, article: artigo.artigo }]),
    onSuccess: async (resultado) => {
      const comTexto = resultado.cancelados.filter((c) => c.devolvidoA === 'sugerido').length;
      message.success(
        comTexto > 0 ? 'Pedido desfeito; o comentário continua lá.' : 'Pedido desfeito.',
      );
      await invalidar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const abrir = (linha: ArtigoNoAdmin) =>
    linha.comentarioId && lei.data
      ? setAberto({ id: linha.comentarioId, lei: lei.data.citation, artigo: linha.artigo })
      : undefined;

  /**
   * A ação da linha, que é sempre uma só.
   *
   * O que está **com um motor** não recebe nenhuma: interromper trabalho alheio
   * por um botão de lista seria a pior forma de descobrir que alguém estava
   * escrevendo.
   */
  const acao = (linha: ArtigoNoAdmin) => {
    if (linha.estado === 'reivindicado') return null;
    if (linha.estado === 'pendente') {
      return (
        <Button size="small" loading={cancelar.isPending} onClick={() => cancelar.mutate(linha)}>
          Remover pedido
        </Button>
      );
    }
    if (linha.temComentario) {
      return (
        <Button size="small" onClick={() => abrir(linha)}>
          Abrir
        </Button>
      );
    }
    return (
      <Button size="small" onClick={() => setPedindo(linha)}>
        Pedir comentário
      </Button>
    );
  };

  const situacao = (linha: ArtigoNoAdmin) =>
    linha.estado ? (
      <Tag color={ROTULO[linha.estado].cor} style={{ marginInlineEnd: 0 }}>
        {ROTULO[linha.estado].texto}
      </Tag>
    ) : (
      <Typography.Text type="secondary">-</Typography.Text>
    );

  if (lei.error) {
    return <Alert type="error" showIcon title={(lei.error as Error).message} />;
  }

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          style={{ padding: 0 }}
          onClick={() => navegar('/legislacao')}
        >
          Legislação Comentada
        </Button>

        {lei.isPending ? (
          <Skeleton active paragraph={{ rows: 1 }} />
        ) : (
          <>
            <Flex align="center" gap={8} wrap style={{ minWidth: 0 }}>
              <Typography.Title level={compacto ? 4 : 3} style={{ margin: 0 }}>
                {lei.data?.citation}
              </Typography.Title>
              <Tag color={lei.data?.publicada ? 'success' : undefined}>
                {lei.data?.publicada ? 'No app' : 'Fora do app'}
              </Tag>
            </Flex>
            <Typography.Text type="secondary">{lei.data?.title}</Typography.Text>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {lei.data?.artigos} artigo(s) · {lei.data?.comentados} comentário(s) no ar
              </Typography.Text>
            </div>
          </>
        )}
      </div>

      <Flex vertical={compacto} gap={8} align={compacto ? 'stretch' : 'center'} wrap>
        <Segmented<RecorteDaLei>
          value={recorte}
          onChange={(valor) => filtrar({ recorte: valor === 'todos' ? undefined : valor })}
          options={RECORTES}
          block={compacto}
        />
        <Input.Search
          allowClear
          defaultValue={termo}
          style={compacto ? { width: '100%' } : { maxWidth: 220 }}
          placeholder="Número do artigo"
          onSearch={(valor) => filtrar({ termo: valor.trim() || undefined })}
        />
      </Flex>

      {lei.isPending ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (lei.data?.itens ?? []).length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            recorte === 'cobrados'
              ? 'Nenhum artigo desta lei foi cobrado nas questões publicadas.'
              : recorte === 'comentados'
                ? 'Nenhum artigo desta lei tem comentário escrito.'
                : 'Nada com esse recorte.'
          }
        />
      ) : compacto ? (
        <List<ArtigoNoAdmin>
          dataSource={lei.data?.itens}
          pagination={false}
          renderItem={(linha) => (
            <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
              <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
                <Flex align="center" gap={8} wrap>
                  <Typography.Text strong>art. {linha.artigo}</Typography.Text>
                  {linha.incidencia > 0 ? (
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {linha.incidencia} questão(ões)
                    </Tag>
                  ) : null}
                  {situacao(linha)}
                </Flex>

                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: 0, fontSize: 12 }}
                  ellipsis={{ rows: 2 }}
                >
                  {linha.texto}
                </Typography.Paragraph>

                {acao(linha)}
              </Flex>
            </List.Item>
          )}
        />
      ) : (
        <Table<ArtigoNoAdmin>
          size="small"
          rowKey="artigo"
          dataSource={lei.data?.itens}
          pagination={false}
          scroll={{ x: LARGURA_MINIMA }}
          columns={[
            {
              title: 'Artigo',
              dataIndex: 'artigo',
              width: LARGURA.artigo,
              render: (artigo: string) => <Typography.Text strong>art. {artigo}</Typography.Text>,
            },
            {
              // O caput, para reconhecer o artigo sem abrir a lei ao lado. Uma
              // linha só: a tela é de escolha, e não de leitura.
              title: 'Texto',
              dataIndex: 'texto',
              ellipsis: true,
              render: (texto: string) => (
                <Typography.Text type="secondary">{texto}</Typography.Text>
              ),
            },
            {
              title: 'Questões',
              dataIndex: 'incidencia',
              width: LARGURA.questoes,
              align: 'right',
              render: (valor: number) =>
                valor > 0 ? (
                  <Tag color="blue">{valor}</Tag>
                ) : (
                  <Typography.Text type="secondary">-</Typography.Text>
                ),
            },
            {
              title: 'Situação',
              width: LARGURA.situacao,
              render: (_valor, linha) => situacao(linha),
            },
            {
              title: '',
              width: LARGURA.acao,
              /*
               * Presa à direita: é a única coluna que se clica, e na janela
               * estreita que ainda mostra a tabela - 768px, o corte do `md` - a
               * sobra não dá para as cinco colunas. Solta, ela seria a primeira
               * a sair pela direita, que foi o defeito que trouxe alguém aqui.
               */
              fixed: 'right',
              render: (_valor, linha) => acao(linha),
            },
          ]}
        />
      )}

      <Flex justify={compacto ? 'center' : 'flex-end'}>
        <Pagination
          current={page}
          pageSize={lei.data?.pageSize ?? porPagina}
          total={lei.data?.total ?? 0}
          size={compacto ? 'small' : undefined}
          showSizeChanger
          pageSizeOptions={TAMANHOS_DE_PAGINA}
          showTotal={(total) => `${total} artigo(s)`}
          onChange={(proxima, tamanho) => {
            // Uma escrita por interação: na troca de tamanho o Antd dispara os
            // dois callbacks, e o segundo apagaria o tamanho novo.
            if (tamanho !== porPagina) {
              filtrar({ porPagina: tamanho === POR_PAGINA_PADRAO ? undefined : tamanho });
              return;
            }
            irParaPagina(proxima);
          }}
        />
      </Flex>

      <Modal
        open={pedindo !== null}
        title={pedindo ? `Pedir comentário do art. ${pedindo.artigo}` : ''}
        okText="Pedir"
        confirmLoading={pedir.isPending}
        onCancel={() => {
          setPedindo(null);
          setBriefing('');
        }}
        onOk={() =>
          pedindo &&
          pedir.mutate({
            artigo: pedindo,
            ...(briefing.trim() ? { briefing: briefing.trim() } : {}),
          })
        }
      >
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            A ressalva chega a quem for escrever. Use-a quando já houve uma tentativa e ela não
            serviu - dizer o que faltou evita que a próxima repita a anterior.
          </Typography.Text>
          {/*
            O artigo sem incidência é o caso que traz alguém a esta tela, e a
            ressalva é onde se diz por que ele importa - o motor não tem como
            deduzir isso de uma contagem que é zero.
          */}
          {pedindo?.incidencia === 0 ? (
            <Alert
              type="info"
              showIcon
              description="Questão nenhuma do catálogo cobra este artigo. Diga na ressalva por que ele vale um comentário."
            />
          ) : null}
          <Input.TextArea
            rows={4}
            value={briefing}
            placeholder="Opcional. Ex.: artigo novo, ainda sem questão, mas central para o tema."
            onChange={(evento) => setBriefing(evento.target.value)}
          />
        </Space>
      </Modal>

      <GavetaDoComentario artigo={aberto} onClose={() => setAberto(null)} />
    </Space>
  );
}
